import React, { useState, useEffect, useRef } from 'react';
// Character limit based on database schema
const MINUTES_CHAR_LIMIT = 255;
import { X } from 'lucide-react';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { supabase } from '../../../../lib/supabase';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import type { Community } from '../../../../types/community';

interface AddMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddMeetingModal({ isOpen, onClose, onSuccess }: AddMeetingModalProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    community: '',
    date: '',
    minutes: ''
  });
  
  // Track character count
  const minutesCharCount = formData.minutes.length;
  
  // Fetch communities with React Query
  const { 
    data: communities, 
    isLoading: isLoadingCommunities, 
    error: communitiesError 
  } = useQuery<Community[]>({
    queryKey: ['communities'],
    queryFn: api.communities.getAll
  });

  // Debug logging for communities
  useEffect(() => {
    if (communitiesError) {
      console.error('Error fetching communities:', communitiesError);
    }
    
    if (communities) {
      console.log('Communities data loaded:', communities);
      console.log('Number of communities:', communities.length);
      
      if (communities.length > 0) {
        console.log('First community:', communities[0]);
      }
    }
  }, [communities, communitiesError]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Create the meeting record
      const { error: insertError } = await supabase
        .from('comm_meetings')
        .insert([{
          meet_comm: formData.community,
          meet_date: formData.date || null,
          meet_minutes: formData.minutes || null
        }]);

      if (insertError) throw insertError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating meeting record:', err);
      setError(err instanceof Error ? err.message : 'Failed to create meeting record');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-0 z-[100] overflow-y-auto"
    >
      <div 
        ref={modalContentRef}
        className="relative bg-white w-full md:max-w-2xl md:rounded-lg md:my-8 min-h-screen md:min-h-0 max-h-full md:max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 md:p-6 border-b bg-white">
          <h3 className="text-xl font-semibold text-gray-900">Add New Meeting Record</h3>
          <button 
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - with scrollable area */}
        <form id="add-meeting-form" onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Meeting Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500">
                  Community *
                </label>
                {/* Display loading indicator if communities are loading */}
                {isLoadingCommunities && (
                  <div className="mt-1 text-sm text-gray-500">Loading communities...</div>
                )}
                
                {/* Display error message if there's an error */}
                {communitiesError && (
                  <div className="mt-1 text-sm text-red-500">
                    Error loading communities: {communitiesError.message}
                  </div>
                )}
                
                {/* Show number of communities loaded for debugging */}
                {communities && communities.length > 0 && (
                  <div className="mt-1 mb-1 text-xs text-gray-400">
                    {communities.length} communities available
                  </div>
                )}
                
                <div className="relative">
                  <select
                    value={formData.community}
                    onChange={(e) => setFormData(prev => ({ ...prev, community: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary appearance-none bg-white cursor-pointer hover:border-primary"
                    required
                    disabled={isLoadingCommunities}
                    style={{ 
                      backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                      backgroundPosition: "right 0.5rem center",
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "1.5em 1.5em",
                      paddingRight: "2.5rem"
                    }}
                  >
                    <option value="">Please select a Community</option>
                    {communities?.map((community) => (
                      <option 
                        key={community.comm_id} 
                        value={community.comm_id}
                        className="py-1"
                      >
                        {community.comm_name} ({community.comm_id})
                      </option>
                    ))}
                  </select>
                  {/* Add a visual indicator for the dropdown */}
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">Select the community for this meeting</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500">
                  Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
                <p className="mt-1 text-xs text-gray-500">Meeting date</p>
              </div>
            </div>
          </div>

          {/* Notes and Docs */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Notes and Docs</h4>
            <div>
              <label className="block text-sm font-medium text-gray-500">
                Notes and Docs
              </label>
              <textarea
                value={formData.minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, minutes: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                placeholder="Enter meeting notes and document links"
                rows={8}
                maxLength={MINUTES_CHAR_LIMIT}
              />
              <div className="flex justify-between">
                <div className="mt-1">
                  <p className="text-xs text-gray-500">
                    Enter detailed information about the meeting (optional, max {MINUTES_CHAR_LIMIT} characters)
                  </p>
                  <p className="text-xs text-gray-500 italic">
                    Add URL to the document repository as needed. URLs will be automatically converted to clickable links when displayed.
                  </p>
                </div>
                <p className={`mt-1 text-xs ${minutesCharCount >= MINUTES_CHAR_LIMIT * 0.9 ? 'text-amber-600' : 'text-gray-500'}`}>
                  {minutesCharCount}/{MINUTES_CHAR_LIMIT}
                </p>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 p-4 md:p-6 border-t bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-meeting-form"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md disabled:opacity-50"
          >
            {isSubmitting ? 'Creating...' : 'Create Meeting'}
          </button>
        </div>
      </div>
    </div>
  );
}
